// office-addin-react - Koppeling van Mozard met Microsoft Office
// Copyright (C) 2021-2022  Mozard BV
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.

/* global Office Word Excel */

import OutlookMailbox from "./OutlookMailbox";

export default class OfficeDocument {
  static getDocumentId() {
    return new Office.Promise((resolve, reject) => {
      if (Office.context.requirements.isSetSupported("WordApi", "1.3")) {
        Word.run(async (context) => {
          const prop =
            context.document.properties.customProperties.getItemOrNullObject("mzdDocumentId");
          context.load(prop);
          await context.sync();
          if (!prop.isNullObject) {
            resolve(prop);
          } else {
            reject("Geen documentnummer gevonden in dit document");
          }
        }).catch(reject);
      } else if (Office.context.requirements.isSetSupported("ExcelApi", "1.5")) {
        console.log("ExcelApi");
        Excel.run(async (context) => {
          console.log("context.workbook", context);
          const prop = context.workbook.properties.custom.getItemOrNullObject("mzdDocumentId");
          context.load(prop);
          await context.sync();
          if (!prop.isNullObject) {
            resolve(prop);
          } else {
            reject("Geen documentnummer gevonden in dit document");
          }
        }).catch(reject);
      } else if (Office.context.requirements.isSetSupported("Mailbox", "1.5")) {
        Office.context.mailbox.item.loadCustomPropertiesAsync(async (context) => {
          console.log(context);
          const customProperties = context.value;
          const prop = customProperties.get("mzdDocumentId");
          if (prop) {
            console.log(prop);
            resolve({ value: prop });
          } else {
            reject("Geen documentnummer gevonden in dit document");
          }
        });
      } else {
        reject("Vereiste Office API voor OfficeDocument.getDocumentId() niet ondersteund");
      }
    });
  }

  static getDocumentTitle(platform) {
    const removeDisallowedChars = (textArr) => {
      let res;
      for (let i = 0; i < textArr.length; i++) {
        const t = textArr.at(i);
        const textLength = t.length;
        const maxLength = 80;
        const prefixLength = "YYYY_MM_DD-".length;
        const max = maxLength - prefixLength;
        const end = textLength < max ? textLength : max;
        const firstChars = t.substring(0, end);
        const disallowedChars = /[:*"<>|%^/\u201c\u201d]/g;
        const tText = firstChars
          .replace(/[\t\r\n]+/g, " ")
          .replace(disallowedChars, "")
          .replace(/\s{2,}/g, " ")
          .trim();
        if (tText) {
          res = tText;
          break;
        }
      }
      return res;
    };

    return new Office.Promise((resolve, reject) => {
      const noSupportMsg = `[${platform}] Vereite Office API voor OfficeDocument.getDocumentTitle() niet ondersteund`;
      if (platform === "Word") {
        if (Office.context.requirements.isSetSupported("WordApi", "1.3")) {
          Word.run(async (context) => {
            const paragraphs = context.document.body.paragraphs;
            paragraphs.load("text");
            await context.sync();
            const paragraphText = [];
            paragraphs.items.forEach((item) => {
              const paragraph = item.text.trim();
              if (paragraph) paragraphText.push(paragraph);
            });
            if (paragraphText.length <= 0) {
              reject("Document bevat geen tekst");
              return;
            }
            const res = removeDisallowedChars(paragraphText);
            if (res) {
              resolve(res);
            } else {
              reject("Document bevat geen bruikbare karakters om te gebruiken als titel");
            }
          }).catch(reject);
        } else {
          reject(noSupportMsg);
        }
      } else if (platform === "Excel") {
        if (Office.context.requirements.isSetSupported("ExcelApi", "1.5")) {
          Excel.run(async (context) => {
            const sheets = context.workbook.worksheets;
            const firstSheet = sheets.getFirst(true);
            const valueRange = firstSheet.getUsedRangeOrNullObject(true);
            valueRange.load("text");
            await context.sync();
            if (valueRange.isNullObject) {
              reject("Document bevat geen waardes");
              return;
            }
            const sheetText = valueRange.text;
            const allText = [];
            sheetText.forEach((row) => {
              row.forEach((cell) => {
                const val = cell.trim();
                if (val) allText.push(val);
              });
            });
            const res = removeDisallowedChars(allText);
            if (res) {
              resolve(res);
            } else {
              reject("Document bevat geen bruikbare karakters om te gebruiken als titel");
            }
          }).catch(reject);
        } else {
          reject(noSupportMsg);
        }
        // } else if (platform === "Outlook") {
        //   if (Office.context.requirements.isSetSupported("Mailbox", "1.5")) {
        //     Excel.run(async (context) => {
        //       const sheets = context.workbook.worksheets;
        //       const firstSheet = sheets.getFirst(true);
        //       const valueRange = firstSheet.getUsedRangeOrNullObject(true);
        //       valueRange.load("text");
        //       await context.sync();
        //       if (valueRange.isNullObject) {
        //         reject("Document bevat geen waardes");
        //         return;
        //       }
        //       const sheetText = valueRange.text;
        //       const allText = [];
        //       sheetText.forEach((row) => {
        //         row.forEach((cell) => {
        //           const val = cell.trim();
        //           if (val) allText.push(val);
        //         });
        //       });
        //       const res = removeDisallowedChars(allText);
        //       if (res) {
        //         resolve(res);
        //       } else {
        //         reject("Document bevat geen bruikbare karakters om te gebruiken als titel");
        //       }
        //     });
        //   } else {
        //     reject(noSupportMsg);
        //   }
      } else {
        reject(`Platform ${platform} wordt niet ondersteund`);
      }
    });
  }

  static setDocumentId(documentId) {
    return new Office.Promise((resolve, reject) => {
      if (Office.context.requirements.isSetSupported("WordApi", "1.3")) {
        Word.run(async (context) => {
          context.document.properties.customProperties.add("mzdDocumentId", documentId);
          context
            .sync()
            .then((res) => {
              resolve(res);
            })
            .catch((e) => {
              reject(e);
            });
        }).catch(reject);
      } else if (Office.context.requirements.isSetSupported("ExcelApi", "1.5")) {
        Excel.run(async (context) => {
          context.workbook.properties.custom.add("mzdDocumentId", documentId);
          context
            .sync()
            .then((res) => {
              resolve(res);
            })
            .catch((e) => {
              reject(e);
            });
        }).catch(reject);
      } else if (Office.context.requirements.isSetSupported("Mailbox", "1.3")) {
        Office.context.mailbox.item.loadCustomPropertiesAsync(async (context) => {
          console.log(context);
          const customProperties = context.value;
          customProperties.set("mzdDocumentId", documentId);
          customProperties.saveAsync((asyncResult) => {
            console.log(asyncResult);
            if (asyncResult.status === "succeeded") {
              console.log(customProperties);
              resolve(Office.context);
            }
          });
        });
      } else {
        reject("Vereiste Office API voor OfficeDocument.setDocumentId() niet ondersteund");
      }
    });
  }

  static getFile(platform, sliceSize) {
    return new Office.Promise((resolve, reject) => {
      if (platform !== "Outlook") {
        Office.context.document.getFileAsync("compressed", { sliceSize }, (result) => {
          // eslint-disable-next-line eqeqeq
          if (result.status == Office.AsyncResultStatus.Succeeded) {
            resolve({
              counter: 0,
              file: result.value,
              sliceCount: result.value.sliceCount,
            });
          } else {
            reject(result.error || new Error(result.status));
          }
        });
      } else {
        OutlookMailbox.getEmail()
          .then((res) => {
            resolve(res);
          })
          .catch((e) => {
            reject(e);
          });
      }
    });
  }

  static getSlice(state) {
    return new Office.Promise((resolve, reject) => {
      state.file.getSliceAsync(state.counter, (result) => {
        // eslint-disable-next-line eqeqeq
        if (result.status == Office.AsyncResultStatus.Succeeded) {
          resolve(result.value);
        } else {
          reject(new Error(result.status));
        }
      });
    });
  }

  static formatSlice(documentProperties, slice, boundary) {
    if (!slice.data) {
      throw new Error("Slice bevat geen data");
    }

    const attachmentContentType = "application/octet-stream";
    const contentDisposition = `Content-Disposition: form-data; name="file"; filename="${documentProperties.documentName}.${documentProperties.documentExtension}"`;

    const requestBodyBeginning = `--------------------------${boundary}\r\nContent-Type: ${attachmentContentType}\r\n${contentDisposition}\r\n\r\n`;
    const requestBodyEnd = `\r\n--------------------------${boundary}--`;

    // Directly create Uint8Array from slice data without Base64 conversion
    const byteArray =
      typeof slice.data === "string"
        ? new TextEncoder().encode(slice.data)
        : new Uint8Array(slice.data);

    const totalRequestSize = requestBodyBeginning.length + byteArray.length + requestBodyEnd.length;

    const uint8array = new Uint8Array(totalRequestSize);

    let offset = 0;

    // Add the request body beginning
    for (let i = 0; i < requestBodyBeginning.length; i++) {
      uint8array[offset++] = requestBodyBeginning.charCodeAt(i);
    }

    // Add the binary attachment
    uint8array.set(byteArray, offset);
    offset += byteArray.length;

    // Add the request body end
    for (let i = 0; i < requestBodyEnd.length; i++) {
      uint8array[offset++] = requestBodyEnd.charCodeAt(i);
    }

    return uint8array;
  }

  static closeFile(state) {
    state.file.closeAsync((result) => {
      if (result.status !== "succeeded") {
        throw new Error("Bestand verzonden, maar kon niet gesloten worden");
      }
    });
  }
}
